import axiosClient from './axiosClient';

export interface FeatureItem {
    key: string;
    name: string;
    category: 'AUTHORING_AI' | 'IN_CLASS_TOOLS' | 'GAMIFICATION' | 'OPERATION_FINANCE';
    description?: string;
    target_roles: string[];
}

export interface PlanItem {
    id: string;
    code: string;
    name: string;
    description?: string;
    target_role: 'TEACHER' | 'ORGANIZATION' | 'PARENT_STUDENT';
    billing_cycle: 'MONTHLY' | 'YEARLY' | 'ON_DEMAND';
    price: number;
    yearly_discount_percent: number;
    monthly_credits: number;
    features: string[];
    quotas: Record<string, any>;
    is_active: boolean;
    is_featured: boolean;
    order_index: number;
    created_at?: string;
    updated_at?: string;
}

export interface SubscriptionItem {
    id: string;
    user_id?: string;
    organization_id?: string;
    plan_id: string;
    plan_code?: string;
    plan_name?: string;
    target_role?: string;
    status: string;
    start_date: string;
    end_date?: string;
    auto_renew: boolean;
    assigned_by: string;
    admin_note?: string;
    custom_monthly_credits?: number;
    extra_features: string[];
    blocked_features: string[];
    custom_quotas: Record<string, any>;
    last_credit_granted_at?: string;
    user_name?: string;
    user_email?: string;
    organization_name?: string;
    organization_owner_email?: string;
    organization_owner_name?: string;
    effective_features: string[];
    effective_monthly_credits: number;
    effective_quotas: Record<string, any>;
}

export interface EffectivePermissions {
    plan_code: string;
    plan_name: string;
    status: string;
    end_date?: string;
    features: string[];
    quotas: Record<string, any>;
    monthly_credits: number;
    is_vip: boolean;
}

export interface CustomSubscriberSample {
    subscription_id: string;
    target_type: string;
    name: string;
    email: string;
    extra_features: string[];
    blocked_features: string[];
    custom_quotas: Record<string, any>;
    custom_monthly_credits?: number;
}

export interface SyncMetrics {
    plan_id: string;
    plan_code: string;
    plan_name: string;
    catalog_features_count: number;
    plan_features_count: number;
    new_features_in_code: string[];
    total_active_subscribers: number;
    standard_subscribers_count: number;
    custom_subscribers_count: number;
    custom_subscribers: CustomSubscriberSample[];
}

export const subscriptionApi = {
    // Master Feature Catalog
    getFeaturesCatalog: () => {
        return axiosClient.get<FeatureItem[]>('/plans/features/catalog');
    },

    // Plan Templates
    getPlans: (params?: { target_role?: string; is_active?: boolean }) => {
        return axiosClient.get<PlanItem[]>('/plans', { params });
    },

    getPlanById: (id: string) => {
        return axiosClient.get<PlanItem>(`/plans/${id}`);
    },

    createPlan: (data: Partial<PlanItem>) => {
        return axiosClient.post<PlanItem>('/plans', data);
    },

    updatePlan: (id: string, data: Partial<PlanItem>) => {
        return axiosClient.put<PlanItem>(`/plans/${id}`, data);
    },

    deletePlan: (id: string) => {
        return axiosClient.delete(`/plans/${id}`);
    },

    // Subscriptions
    getSubscriptions: (params?: { target_role?: string; status?: string; search?: string }) => {
        return axiosClient.get<SubscriptionItem[]>('/subscriptions', { params });
    },

    getEffectivePermissions: (orgId?: string, params?: { lesson_id?: string; class_id?: string }) => {
        return axiosClient.get<EffectivePermissions>('/subscriptions/effective-permissions', {
            params: {
                ...(orgId ? { organization_id: orgId } : {}),
                ...(params || {})
            }
        });
    },

    assignSubscription: (data: {
        user_id?: string;
        organization_id?: string;
        plan_id: string;
        duration_months?: number;
        custom_monthly_credits?: number;
        extra_features?: string[];
        blocked_features?: string[];
        custom_quotas?: Record<string, any>;
        admin_note?: string;
    }) => {
        return axiosClient.post<SubscriptionItem>('/subscriptions/assign', data);
    },

    updateSubscription: (id: string, data: Partial<SubscriptionItem>) => {
        return axiosClient.put<SubscriptionItem>(`/subscriptions/${id}`, data);
    },

    grantCredits: (id: string) => {
        return axiosClient.post<{ success: boolean; message: string; granted_credits: number }>(
            `/subscriptions/${id}/grant-credits`
        );
    },

    getSyncMetrics: (planId: string) => {
        return axiosClient.get<SyncMetrics>('/subscriptions/sync-metrics', {
            params: { plan_id: planId }
        });
    },

    syncFeatures: (data: { plan_id: string; mode: string; target_subscription_ids?: string[] }) => {
        return axiosClient.post<{
            success: boolean;
            message: string;
            mode: string;
            plan_code: string;
            synced_features: string[];
            total_subscribers: number;
            reset_count: number;
        }>('/subscriptions/sync-features', data);
    }
};

export default subscriptionApi;

